import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search } from 'lucide-react';
import { Task, TaskCreate, TaskUpdate, TaskStatus, TaskPriority } from '@/app/types/task';
import { useQuery } from '@tanstack/react-query';
import { PartySelector } from '@/app/components/common/PartySelector';
import { listUsers } from '@/app/services/userManagementService';
import { useRole } from '@/app/hooks/useRole';
import { useAuthStore } from '@/app/store/authStore';
import { searchItemsAsync, getAllItemsAsync, EnrichedItem } from '@/lib/indexeddb/database';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// Zod Schema for Task Form
const formSchema = z.object({
  title: z.string().min(1, 'Task title is required').max(200),
  description: z.string().optional().nullable(),
  status: z.nativeEnum(TaskStatus).optional(),
  priority: z.nativeEnum(TaskPriority).optional(),
  due_date: z.string().optional().nullable(),
  assigned_to_user_id: z.number().optional().nullable(),
  parent_entity_id: z.number().optional().nullable(),
  related_entity_type: z.string().optional().nullable(),
  related_entity_id: z.number().optional().nullable(),
  task_category: z.string().optional().nullable(),
  task_type: z.string().optional().nullable(),
});

type TaskFormValues = z.infer<typeof formSchema>;

interface TaskFormProps {
  onSubmit: (data: TaskCreate | TaskUpdate) => void;
  defaultValues?: Partial<Task>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  prefilledPartyId?: number;
  defaultTaskType?: string;
}

/**
 * TaskForm Component
 *
 * Shared form component for creating and editing tasks
 * Uses React Hook Form with Zod validation
 *
 * @param onSubmit - Callback function when form is submitted
 * @param defaultValues - Default values for edit mode
 * @param isLoading - Loading state for submit button
 * @param mode - 'create' or 'edit' mode
 * @param prefilledPartyId - Pre-filled party ID for quick task creation
 */
export const TaskForm: React.FC<TaskFormProps> = ({
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  prefilledPartyId,
  defaultTaskType,
}) => {
  const { isClientManager } = useRole();
  const user = useAuthStore((state) => state.user);
  const userOrgId = useAuthStore((state) => state.userOrgId);

  // Item search state
  const [titleSearchOpen, setTitleSearchOpen] = useState(false);
  const [titleSearchQuery, setTitleSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EnrichedItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(titleSearchQuery, 200);

  // IndexedDB sync hook
  const { isInitialized: isIndexedDBInitialized } = useSQLiteSync();

  // Load initial items when popover opens
  useEffect(() => {
    if (titleSearchOpen && isIndexedDBInitialized && userOrgId && !titleSearchQuery) {
      const loadInitialItems = async () => {
        setIsSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          // Get first 10 active items
          const initialItems = allItems.filter(item => item.is_active).slice(0, 10);
          setSearchResults(initialItems);
        } catch (error) {
          console.error('[TaskForm] Error loading initial items:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [titleSearchOpen, isIndexedDBInitialized, userOrgId, titleSearchQuery]);

  // Search items when query changes
  useEffect(() => {
    if (debouncedSearchQuery.length > 1 && isIndexedDBInitialized && userOrgId) {
      const performSearch = async () => {
        setIsSearching(true);
        try {
          const results = await searchItemsAsync(debouncedSearchQuery, userOrgId, 50);
          setSearchResults(results);
        } catch (error) {
          console.error('[TaskForm] Item search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      performSearch();
    } else if (debouncedSearchQuery.length === 0 && titleSearchOpen && isIndexedDBInitialized && userOrgId) {
      // Reload initial items when search is cleared
      const loadInitialItems = async () => {
        setIsSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 10);
          setSearchResults(initialItems);
        } catch (error) {
          console.error('[TaskForm] Error loading initial items:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [debouncedSearchQuery, isIndexedDBInitialized, userOrgId, titleSearchOpen]);

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: defaultValues?.title || '',
      description: defaultValues?.description || '',
      status: defaultValues?.status || TaskStatus.OPEN,
      priority: defaultValues?.priority || TaskPriority.MEDIUM,
      due_date: defaultValues?.due_date || '',
      assigned_to_user_id: defaultValues?.assigned_to_user_id ?? (mode === 'create' && user?.id ? parseInt(user.id) : null),
      parent_entity_id: defaultValues?.parent_entity_id || prefilledPartyId || null,
      related_entity_type: defaultValues?.related_entity_type || null,
      related_entity_id: defaultValues?.related_entity_id || null,
      task_category: defaultValues?.task_category || '',
      task_type: defaultValues?.task_type || defaultTaskType || null,
    },
  });

  // Update parent_entity_id when prefilledPartyId changes
  useEffect(() => {
    if (prefilledPartyId && mode === 'create') {
      form.setValue('parent_entity_id', prefilledPartyId);
    }
  }, [prefilledPartyId, mode, form]);


  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: () => listUsers({ limit: 100, skip: 0 }),
  });

  const handleSubmit: SubmitHandler<TaskFormValues> = (values) => {
    // If a party is selected, set entity type and id; otherwise send null
    const hasParty = values.parent_entity_id != null;

    // Clean up data before submitting
    const dataToSend: any = {
      title: values.title,
      description: values.description || null,
      status: values.status,
      priority: values.priority,
      due_date: values.due_date || null,
      assigned_to_user_id: values.assigned_to_user_id || null,
      parent_entity_type: hasParty ? 'party' : null,
      parent_entity_id: hasParty ? values.parent_entity_id : null,
      related_entity_type: values.related_entity_id ? (values.related_entity_type || 'item') : null,
      related_entity_id: values.related_entity_id || null,
      task_category: values.task_category || null,
      task_type: values.task_type || null,
    };

    // In create mode, include all fields
    // In edit mode, only include changed fields
    onSubmit(mode === 'edit' ? dataToSend : dataToSend);
  };

  const isEnquiry = defaultTaskType === 'enquiry';
  const isTodo = defaultTaskType === 'todo';

  const submitLabel = mode === 'edit'
    ? (isTodo ? 'Update To-Do' : isEnquiry ? 'Update Enquiry' : 'Update Task')
    : (isTodo ? 'Create To-Do' : isEnquiry ? 'Create Enquiry' : 'Create Task');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">

        {/* === To-Do simplified layout: title, description, assignee === */}
        {isTodo ? (
          <>
            {/* Title - plain text input */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="What needs to be done?" autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ''}
                      placeholder="Add details or notes..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Assigned To */}
            <FormField
              control={form.control}
              name="assigned_to_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assigned To</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === 'unassigned' ? null : parseInt(value))}
                    value={field.value?.toString() || 'unassigned'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {usersData?.items?.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.full_name || user.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : isEnquiry ? (
          <>
            {/* Title - plain text input */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enquiry subject..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description - multiline */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ''}
                      placeholder="Enquiry details, customer requirements, notes..."
                      rows={5}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Assigned To */}
            <FormField
              control={form.control}
              name="assigned_to_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assigned To</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === 'unassigned' ? null : parseInt(value))}
                    value={field.value?.toString() || 'unassigned'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {usersData?.items?.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.full_name || user.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category - optional */}
            <FormField
              control={form.control}
              name="task_category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} placeholder="e.g., Product, Pricing, General" />
                  </FormControl>
                  <FormDescription>Optional</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <>
            {/* === Generic task full layout === */}

            {/* Client - Using PartySelector filtered by Client */}
            <FormField
              control={form.control}
              name="parent_entity_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client*</FormLabel>
                  <PartySelector
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? null)}
                    placeholder="Select a client..."
                    filterPartyGroup="Client"
                  />
                  <FormDescription>Select a client for this task</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Title - Item Search Dropdown */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Title (Item)*</FormLabel>
                  <Popover open={titleSearchOpen} onOpenChange={setTitleSearchOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={titleSearchOpen}
                          className="w-full justify-between font-normal"
                        >
                          {field.value || "Search and select an item..."}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Type item name, code, or description..."
                          value={titleSearchQuery}
                          onValueChange={setTitleSearchQuery}
                        />
                        <CommandList className="max-h-[300px]">
                          {isSearching && (
                            <div className="p-4 text-center">
                              <Loader2 className="h-4 w-4 animate-spin inline-block" />
                            </div>
                          )}
                          {!isSearching && searchResults.length === 0 && (
                            <CommandEmpty>No items found.</CommandEmpty>
                          )}
                          <CommandGroup>
                            {searchResults.map((item) => (
                              <CommandItem
                                key={item.id}
                                value={`item-${item.id}`}
                                onSelect={() => {
                                  field.onChange(item.name);
                                  form.setValue('related_entity_type', 'item');
                                  form.setValue('related_entity_id', item.id);
                                  setTitleSearchOpen(false);
                                  setTitleSearchQuery('');
                                }}
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{item.name}</span>
                                  {item.sku && (
                                    <span className="text-xs text-muted-foreground">
                                      Code: {item.sku}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormDescription>Search for an item from your local inventory</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ''}
                      placeholder="Add task details, notes, or instructions..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Priority and Status Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Priority */}
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={TaskPriority.LOW}>Low</SelectItem>
                        <SelectItem value={TaskPriority.MEDIUM}>Medium</SelectItem>
                        <SelectItem value={TaskPriority.HIGH}>High</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Status */}
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={TaskStatus.OPEN}>Open</SelectItem>
                        <SelectItem value={TaskStatus.COMPLETED}>Completed</SelectItem>
                        <SelectItem value={TaskStatus.CANCELLED}>Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Due Date and Assigned To Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Due Date */}
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Assigned To */}
              <FormField
                control={form.control}
                name="assigned_to_user_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assigned To</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === 'unassigned' ? null : parseInt(value))}
                      value={field.value?.toString() || 'unassigned'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {usersData?.items?.map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.full_name || user.username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Task Type - only show when no defaultTaskType is set (general Tasks page) */}
            {!defaultTaskType && (
              <FormField
                control={form.control}
                name="task_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="todo">Todo</SelectItem>
                        <SelectItem value="enquiry">Enquiry</SelectItem>
                        <SelectItem value="follow_up">Follow Up</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                        <SelectItem value="appointment">Appointment</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>Optional: classify the type of task</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Category */}
            <FormField
              control={form.control}
              name="task_category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} placeholder="e.g., Follow-up, Support, Sales" />
                  </FormControl>
                  <FormDescription>Optional: categorize your task for better organization</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};
